import { useEffect, useRef, useState } from 'react';
import { Err, ago } from '../ui.js';
import { startResearch, listResearchJobs, getResearchJob, ApiError } from '../api.js';
import type { AgentJob } from '../api.js';

// The research agent's face.
//
// A run takes minutes and happens in a background function, so the only honest
// UI is one that keeps saying what it is doing. A spinner would be a lie: it
// looks the same whether the agent is reading its fourth search result or died
// nine minutes ago without telling anyone.
//
// It polls every four seconds while a run is live and stops the moment it is
// not. Polling a finished job forever is how a page quietly burns a laptop
// battery, and there is nothing to learn after `done`.

const LIVE = ['queued', 'running'];

export function AgentRun({ icpId, onFound }: {
  icpId: string | null;
  onFound: () => void;
}) {
  const [job, setJob] = useState<AgentJob | null>(null);
  const [want, setWant] = useState(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  // Pick up a run already in flight — a refresh mid-run should not lose it.
  // That is the entire reason the job lives in the database and not in state.
  useEffect(() => {
    listResearchJobs()
      .then((r) => setJob(r.jobs[0] ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!job || !LIVE.includes(job.status)) {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
      return;
    }
    timer.current = window.setInterval(() => {
      getResearchJob(job.id)
        .then((r) => {
          setJob(r.job);
          if (r.job.status === 'done' && r.job.found > 0) onFound();
        })
        .catch(() => {});
    }, 4000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [job?.id, job?.status, onFound]);

  async function run() {
    setBusy(true); setError(null);
    try {
      const r = await startResearch({ icp_id: icpId ?? undefined, want });
      setJob({
        id: r.job_id, status: 'queued', progress: 'Starting…', found: 0, error: null,
        searches: 0, in_tokens: 0, out_tokens: 0,
        created_at: new Date().toISOString(), finished_at: null, output: {},
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  const live = job && LIVE.includes(job.status);

  return (
    <section className="panel agentrun">
      <h4>Send an agent to find them</h4>
      <p className="meta">
        It searches the web against your ICP — the profile, its disqualifiers, the
        titles from your personas, and any companies you named as lookalikes —
        reads what it finds, and stages candidates above with a cited reason. It
        proposes; you promote. Nothing becomes an account on its own.
      </p>

      <Err>{error}</Err>

      {!live && (
        <div className="toolbar">
          <label className="check">
            Find
            <select value={want} onChange={(e) => setWant(Number(e.target.value))}>
              {[5, 10, 15, 20, 25].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            companies
          </label>
          <button className="primary" disabled={busy} onClick={() => void run()}>
            {busy ? 'Starting…' : 'Run the agent'}
          </button>
          {/* Spend is visible, before it is spent. */}
          <span className="cost">web search + tokens — a few minutes</span>
        </div>
      )}

      {live && (
        <div className="agentlive">
          <span className="agentdot" aria-hidden="true" />
          <div className="agentlive-text">
            <strong>{job.progress ?? 'Working…'}</strong>
            <span className="meta">
              Started {ago(job.created_at)}
              {job.searches > 0 && ` · ${job.searches} search${job.searches === 1 ? '' : 'es'}`}
              {' · you can leave this page, it keeps running'}
            </span>
          </div>
        </div>
      )}

      {job && !live && job.status !== 'cancelled' && (
        <div className={`agentdone ${job.status === 'failed' ? 'bad' : ''}`}>
          <strong>
            {job.status === 'failed'
              ? 'That run failed'
              : job.found > 0
                ? `${job.found} new candidate${job.found === 1 ? '' : 's'} staged`
                : 'Nothing new found'}
          </strong>
          {job.error && <span className="meta">{job.error}</span>}
          {job.output?.notes && <span className="meta">{job.output.notes}</span>}
          {/* Proposed-versus-written is the dedupe doing its job, and worth
              seeing: it means the agent found companies you already have. */}
          {job.status === 'done' && (job.output?.proposed ?? 0) > job.found && (
            <span className="meta">
              {job.output.proposed} proposed · {(job.output.proposed ?? 0) - job.found} already
              in the CRM
            </span>
          )}
          {job.searches > 0 && (
            <span className="meta">
              {job.searches} searches · {(job.in_tokens + job.out_tokens).toLocaleString()} tokens
              {job.finished_at && ` · finished ${ago(job.finished_at)}`}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
